/**
 * Lists the element categories shown in the editor's Elements panel (shapes, frames,
 * logos, 3D). Used by the Elements panel UI to render category tiles and switch views.
 * Mental model: this is just static display data, not layer or canvas state.
 */

import type { ElementCategory } from '@/types/ui/element';

const GALLERY_BASE =
  'https://s3.amazonaws.com/deltax.testing/BPs/10367/CreativeBuilder/Gallery/Images';

export const BUILT_IN_CATEGORY_NAMES: readonly string[] = [
  'Shapes',
  'Frames',
  '3D',
  'Logos',
  'Background'
];

/** Lower-cased lookup for the name checks above. */
export const BUILT_IN_CATEGORY_KEYS: ReadonlySet<string> = new Set(
  BUILT_IN_CATEGORY_NAMES.map(name => name.trim().toLowerCase())
);

export const ELEMENT_CATEGORIES: ElementCategory[] = [
  {
    id: 'background',
    name: 'Background',
    image: {
      light: `${GALLERY_BASE}/background%20lite%20mode_639275602691378843.svg`,
      dark: `${GALLERY_BASE}/background%20dark%20mode_639275611197926608.svg`
    }
  },
  {
    id: 'shapes',
    name: 'Shapes',
    image: {
      light: `${GALLERY_BASE}/Shapes%20light%20mode_639221290149391018.svg`,
      dark: `${GALLERY_BASE}/shapes%20dark%20mode_639221290149287524.svg`
    }
  },
  {
    id: 'frames',
    name: 'Frames',
    curated: true,
    image: {
      light: `${GALLERY_BASE}/frame%20light%20mode_639221290059866311.svg`,
      dark: `${GALLERY_BASE}/frame%20dark%20mode_639221290150769047.svg`
    }
  },
  {
    id: '3d',
    name: '3D',
    image: {
      light: `${GALLERY_BASE}/3D%20light%20mode_639221290149297450.svg`,
      dark: `${GALLERY_BASE}/3D%20dark%20mode_639221290149320508.svg`
    }
  },
  {
    id: 'logos',
    name: 'Logos',
    image: {
      light: `${GALLERY_BASE}/logo%20light%20mode_639221290149289831.svg`,
      dark: `${GALLERY_BASE}/logo%20dark%20mode_639221290149331906.svg`
    }
  }
];

/**
 * Curated built-ins, keyed like `BUILT_IN_CATEGORY_KEYS`. The server carries no
 * curated flag, so a seeded category is matched by `isDefault` plus its name.
 */
export const CURATED_BUILT_IN_KEYS: ReadonlySet<string> = new Set(
  ELEMENT_CATEGORIES.filter(category => category.curated).map(category =>
    category.name.trim().toLowerCase()
  )
);

export const RETIRED_BUILT_IN_KEYS: ReadonlySet<string> = new Set(['grid']);

/** Built-ins left out of "Save as element": their panels have no list of saved elements. */
export const SAVE_HIDDEN_BUILT_IN_KEYS: ReadonlySet<string> = new Set([
  'background'
]);
